import React, { useState, useEffect, useMemo, useRef } from "react";
import useApi from "../../hooks/useApiGetPost";

// Placeholder imports (assuming these exist in the actual project structure)
import useApiAthul from "../../hooks/useApi";
import groupSalesThreeLevels from "../utils/groupSalesThreeLevels";

// 🚨 NEW COMPONENT IMPORTS
import InvoiceProfitTable from "./InvoiceProfitTable";
import InvoiceProfitItemWise from "./InvoiceProfitItemWise";

import SalesTableFlatCASH from "../sales_analysis_report/SalesTableFlatCASH";
import SalesTableMonthly from "../sales_analysis_report/SalesTableMonthly";
import SalesTableAggregated from "../sales_analysis_report/SalesTableAggregated";
import SalesTableDefault from "../sales_analysis_report/SalesTableDefault";

import SalesTableGroupedPDF from "../sales_analysis_report/SalesTableGroupedPDF";
import SalesTableAggregatedPDF from "../sales_analysis_report/SalesTableAggregatedPDF";
import SalesTableMonthlyPDF from "../sales_analysis_report/SalesTableMonthlyPDF";
import SalesTableFlatCASHPDF from "../sales_analysis_report/SalesTableFlatCASHPDF";
import SalesTableDefaultPDF from "../sales_analysis_report/SalesTableDefaultPDF";

import InvoiceProfitAnalysisReportPDF from "./InvoiceProfitAnalysisReportPDF";

import { pdf } from "@react-pdf/renderer";
import ReportDownloadButton from "../sales_analysis_report/ReportDownloadButton";
import DynamicTableForInvoiceProfitAnalysis from "./DynamicTableForInvoiceProfitAnalysis";

// --- Lightweight Tailwind form primitives (styled to match TrialBalanceGroup report look) ---

const FieldLabel = ({ children }) => (
  <label className="block text-[11px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
    {children}
  </label>
);

const TextInput = ({ className = "", ...props }) => (
  <input
    {...props}
    className={`w-full bg-white border border-gray-300 rounded-md px-2.5 py-1.5 text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 ${className}`}
  />
);

const SelectInput = ({ className = "", children, ...props }) => (
  <select
    {...props}
    className={`w-full bg-white border border-gray-300 rounded-md px-2.5 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 ${className}`}
  >
    {children}
  </select>
);

const Spinner = () => (
  <svg
    className="animate-spin h-4 w-4 text-gray-400"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
    />
  </svg>
);

// Minimal typeahead replacement for the old MUI Autocomplete, styled to match the report's inputs.
function SimpleAutocomplete({
  label,
  options,
  getOptionLabel,
  value,
  onChange,
  loading,
  placeholder,
  width = "w-[200px]",
}) {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(value ? getOptionLabel(value) || "" : "");
  }, [value]);

  const filtered = (options || []).filter((opt) =>
    (getOptionLabel(opt) || "").toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className={`relative ${width}`}>
      <FieldLabel>{label}</FieldLabel>
      <div className="relative">
        <TextInput
          type="text"
          value={query}
          placeholder={placeholder || "Type to search..."}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
        />
        {loading && (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <Spinner />
          </div>
        )}
      </div>
      {open && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {filtered.map((opt, i) => (
            <div
              key={i}
              onMouseDown={() => {
                onChange(opt);
                setQuery(getOptionLabel(opt) || "");
                setOpen(false);
              }}
              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-violet-50 cursor-pointer"
            >
              {getOptionLabel(opt)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Utility Functions (Keep these in your main component or move to a shared file) ---

const formatNumber = (val) =>
  Number(val || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

// The keys in your data are likely 'PROFIT', 'COST', 'QTY', 'VAT_AMT', etc.
// Ensure this utility function covers all numeric keys you want to total.
const isNumericHeader = (header) =>
  [
    "AMT",
    "COMMISSION",
    "RETURN",
    "VAT",
    "TOTAL",
    "PROFIT",
    "COST",
    "QTY",
    "AMOUNT",
    "SRNO",
    "VAT_AMT",
    "VAT AMT",
    "TOTAL_AMT",
    "GROSS SALE",
    "DISC",
    "NET SALES",
  ].some((s) => header.toUpperCase().includes(s));

// --- Helper Functions (Stubs for the original dynamic logic) ---

// Placeholder for logic that determines the key for the first column in aggregated modes
const getDescriptiveColumnKey = (mode, data) => {
  if (!data || data.length === 0) return "CODE";

  // In non-grouped/non-aggregated modes (like INVPROF or ITMPROF),
  // the first column might be the Invoice No or Item Code.
  if (mode === "SALES_CUSTOMER") return "ACCOUNT";
  if (mode === "SALES_SALESMAN") return "SALESPERSON";

  // Find the first key that is NOT a numeric header and NOT an internal grouping key
  const descriptiveKey = Object.keys(data[0]).find(
    (key) =>
      !isNumericHeader(key) &&
      !["isGroupHeader", "isSubHeader", "totalAmount"].includes(key)
  );

  // Fallback to the first non-numeric column found, or a default.
  return (
    descriptiveKey ||
    Object.keys(data[0]).find((key) => !isNumericHeader(key)) ||
    "CODE"
  );
};

// Placeholder for grandColumnTotals calculation (kept for PDF usage, but not passed to DynamicTable)
const calculateGrandTotals = (data) => {
  if (!data || data.length === 0) return {};
  const totals = {};
  const firstRow = data[0];

  Object.keys(firstRow).forEach((key) => {
    // Simple check: if the key contains numbers in the first row, aggregate it
    if (typeof firstRow[key] === "number" || isNumericHeader(key)) {
      totals[key] = data.reduce((sum, row) => sum + Number(row[key] || 0), 0);
    }
  });
  return totals;
};

// Placeholder for page and rowsPerPage logic (if you decide to implement this in the parent)
const usePagination = (data) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Slice the data for the current page
  const paginatedRows = data.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return {
    paginatedRows,
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
  };
};

const useScrollLoading = (data) => {
  // 25 is the default number of rows to load at once
  const defaultRowsPerPage = 1000;
  const [rowsToDisplay, setRowsToDisplay] = useState(defaultRowsPerPage);

  // Function to load the next set of data
  const loadNextPage = () => {
    setRowsToDisplay((prev) =>
      Math.min(prev + defaultRowsPerPage, data.length)
    );
  };

  // Reset the loaded count whenever the underlying filtered data array changes
  useEffect(() => {
    // Reset rows to display if the data is available, otherwise set to 0.
    setRowsToDisplay(data.length > 0 ? defaultRowsPerPage : 0);
  }, [data.length]);

  const paginatedRows = data.slice(0, rowsToDisplay);
  const hasMore = rowsToDisplay < data.length;

  return { paginatedRows, loadNextPage, hasMore, totalRows: data.length };
};

const InvoiceProfitAnalysisReport = () => {


  let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
  ? JSON.parse(localStorage.getItem("apiPaths"))
  : "-";

  let BASE_URL = "https://cubixweberp.com:213";
  const API_PATHS = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : [{ API_CONFIG: "https://cubixweberp.com:213" }];
  const COMPANY_CODE = localStorage.getItem("companyCode") || "-";

  console.log("API_PATHS -->", API_PATHS);
  if (API_PATHS) {
    if (API_PATHS?.length > 0) {
      BASE_URL = API_PATHS[0].API_CONFIG;
    }
  }

  const [text_search_customer, setTextSearchCustomer] = useState("");

  const [searchTermCustomer, setSearchTerm] = useState("");

  const { request, loading } = useApiAthul();

  const [current_statusOfReturnCheckbox, setCurrentStatusOfReturnCheckbox] =
    useState("NORET");
  const [current_statusOfFifoCheckbox, setCurrentStatusOfFifoCheckbox] =
    useState(false);

  const [showItemList, setShowItemList] = useState(false);

  // --- State Variables ---
  const [searchTextTable, setSearchTextTable] = useState("");
  const [currentSelectedItem, setCurrentSelectedItem] = useState("-");
  const [modalSuggestions, setModalSuggestions] = useState([]);
  const [searchBy, setSearchBy] = useState("UNIVERSAL");

  const salesModes = [
    { label: "Invoice wise Profit", value: "INVPROF" },

    { label: "Item wise Profit", value: "ITMPROF" },

    { label: "Group Wise Profit", value: "GROUPPROF" },

    { label: "Categorywise Profit", value: "CATPROF" },

    { label: "Salesmanwise Profit", value: "INVPROF_SALESMAN" },

    { label: "Departmentwise Profit", value: "INVPROF_DEPT" },

    { label: "SubGroupWise", value: "SGROUPPROF" },

    { label: "SubCategorywise", value: "SCATPROF" },

    { label: "Itemwise Summary", value: "ITMPROF_SUMM" },

    { label: "Customerwise Profit", value: "CUST_PROF" },

    { label: "Item-Customerwise Profit", value: "ITMCUSTPROF_SUMM" },
  ];

  const [currentSelectedModeObject, setCurrentSelectedModeObject] =
    useState(null);
  const [currentSelectedDepartment, setCurrentSelectedDepartment] =
    useState("-");
  const [userDepartmentList, setUserDepartment] = useState(null);
  const [currentSelectedCategory, setCurrentSelectedCategory] = useState("-");
  const [currentSelectedSubCategory, setCurrentSelectedSubCategory] =
    useState("-");
  const [currentSelectedGroup, setCurrentSelectedGroup] = useState("-");
  const [currentSelectedSubGroup, setCurrentSelectedSubGroup] = useState("-");

  const {
    loading: loadinggetSubCategory,
    data: datagetSubCategory,
    get: getSubCategory,
  } = useApi();
  const {
    loading: loadinggetCategory,
    data: datagetCategory,
    get: getCategory,
  } = useApi();
  const {
    loading: loadinggetSubgroup,
    data: datagetSubgroup,
    get: getSubgroup,
  } = useApi();
  const {
    loading: loadinggetgroup,
    data: datagetgroup,
    get: getgroup,
  } = useApi();
  const {
    loading: loadinggetCustomerList,
    data: datagetCustomerList,
    get: getCustomerList,
  } = useApi();

  const [customerListData, setCustomerListData] = useState(null);

  useEffect(() => {
    if (datagetCustomerList) {
      setCustomerListData(datagetCustomerList);
    }
  }, [datagetCustomerList]);

  const {
    data: datagetSalesOrderReportAPI,
    loading: loadinggetSalesOrderReportAPI,
    get: getSalesOrderReportAPI,
  } = useApi();
  const { data: datagetgetFinancialDateApi, get: getFinancialDateApi } =
    useApi();
  const { data: datagetJobList, get: getJobList } = useApi();
  const { data: datagetSalesmanList, get: getSalesmanList } = useApi();

  const [isFilterPanelVisible, setIsFilterPanelVisible] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [itemSearch, setItemSearch] = useState("");
  const [jobCode, setJobCode] = useState(null);
  const [chipSearch, setChipSearch] = useState("");
  const [selectedSalesMan, setSelectedSalesMan] = useState("");

  // --- Effects & Handlers ---

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem("currentUser"));
    setUserDepartment(currentUser || null);

    let currentDepartment = localStorage.getItem("currentSelectedDepartment");

    if (
      !currentDepartment ||
      currentDepartment?.trim() === "" ||
      currentDepartment === "null"
    ) {
      currentDepartment = currentUser?.[0]?.DEPTNO || "";
    }

    setCurrentSelectedDepartment(currentDepartment);

    const today = new Date().toLocaleDateString("en-CA");
    setDateFrom(today);
    setDateTo(today);

    getCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/category/-/-`
    );
    getSubCategory(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subcategory/-/-`
    );
    getgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/group/-/-`
    );
    getSubgroup(
      `${API_PATHS[0].API_CONFIG}/api/mastercount/${COMPANY_CODE}/subgroup/-/-`
    );
    getJobList(
      `${API_PATHS[0].API_CONFIG}/api/cpayscount/${COMPANY_CODE}/joblist/-/-`
    );

    if (text_search_customer?.trim()?.length != 0) {
      const currentUser = JSON.parse(localStorage.getItem("currentUser"));

      let DEPTNO = currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-";
      getCustomerList(
        `${API_PATHS[0].API_CONFIG}/api/Search_Customer/${COMPANY_CODE}/Cust/${text_search_customer}/${DEPTNO}`
      );
    }

    let departmentToPassToAPI = "-";

    if (currentSelectedDepartment) {
      departmentToPassToAPI = currentSelectedDepartment;
    }

    getSalesmanList(
      `${API_PATHS[0].API_CONFIG}/api/MasterCount/${COMPANY_CODE}/SALESPERSONLIST/-/${departmentToPassToAPI}`
    );

    handleChipClick({ label: "Invoice wise Profit", value: "INVPROF" });
  }, []);

  const handleChipClick = (chip) => {
    setCurrentSelectedModeObject(chip);
    setSelectedFilters((prev) => (prev.includes(chip) ? [] : [chip]));
  };

  const resetFilters = () => {
    // FINANCIAL YEAR START DATE LOGIC
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }

    const today = new Date().toLocaleDateString("en-CA");
    setDateTo(today);

    handleChipClick({ label: "Invoice wise Profit", value: "INVPROF" });
    setItemSearch("");
    setCurrentSelectedGroup("-");
    setCurrentSelectedSubGroup("-");
    setCurrentSelectedCategory("-");
    setCurrentSelectedSubCategory("-");
    setJobCode(null);
    setChipSearch("");
  };

  const filteredChips = salesModes.filter((chip) =>
    chip.label.toLowerCase().includes(chipSearch.toLowerCase())
  );

  useEffect(() => {
    if (
      currentSelectedModeObject &&
      dateFrom &&
      dateTo &&
      currentSelectedDepartment
    ) {
      let mode_to_pass_to_api = currentSelectedModeObject
        ? currentSelectedModeObject.value
        : "-";
      let job_code_to_pass_to_api = jobCode ? jobCode.JobCode : "-";
      let customer_account_to_api =
        currentSelectedItem != "-" ? currentSelectedItem?.account?.trim() : "-";

      let ret_status_for_api = current_statusOfReturnCheckbox;

      let fifo_status_for_api = "N";
      if (current_statusOfFifoCheckbox === true) {
        fifo_status_for_api = "N";
      } else {
        fifo_status_for_api = "N"; // what to pass - or anything else now put N just for testing
      }

      let apiUrl = `${API_PATHS[0].API_CONFIG}/api/CRMInvoiceProfitAnalysis/${COMPANY_CODE}/${mode_to_pass_to_api}/${dateFrom}/${dateTo}/${currentSelectedGroup}/${currentSelectedSubGroup}/${currentSelectedCategory}/${currentSelectedSubCategory}/-/-/${ret_status_for_api}/${currentSelectedDepartment}/${customer_account_to_api}/-/${fifo_status_for_api}`;

      getSalesOrderReportAPI(apiUrl);
    }
  }, [
    currentSelectedModeObject,
    dateFrom,
    dateTo,
    currentSelectedDepartment,
    currentSelectedCategory,
    currentSelectedGroup,
    currentSelectedSubGroup,
    currentSelectedSubCategory,
    currentSelectedItem,
    jobCode,
    current_statusOfReturnCheckbox,
    current_statusOfFifoCheckbox,
  ]);

  useEffect(() => {
    getFinancialDateApi(
      `${API_PATHS[0].API_CONFIG}/api/Cpayscount/${COMPANY_CODE}/companyinfo/-/-`
    );
  }, []);

  // FINANCIAL YEAR START DATE LOGIC
  useEffect(() => {
    if (datagetgetFinancialDateApi) {
      const fyStart = datagetgetFinancialDateApi[0]?.fystart?.split("T")[0];
      setDateFrom(fyStart);
    }
  }, [datagetgetFinancialDateApi]);

  const fetchProductData = async (
    query = "-",
    searchByValue = searchBy,
    location = "A"
  ) => {
    const formattedQuery = query.replace(/\//g, "%2F");
    try {
      const url = `${BASE_URL}/api/CRMStockList/${COMPANY_CODE}/${searchByValue}/${formattedQuery}/${location}/-`;
      const response = await request({ url, method: "GET", timeout: 5000 });
      return response || [];
    } catch (err) {
      console.error("Failed to fetch product data", err);
      return [];
    }
  };

  const getInitialProductData = async () => {
    setModalSuggestions(await fetchProductData("-"));
  };
  useEffect(() => {
    getInitialProductData();
  }, []);

  const handleModalSearchQueryChange = async (query = "-") => {
    setItemSearch(query);
    setModalSuggestions(await fetchProductData(query));
  };

  useEffect(() => {
    if (searchTermCustomer?.length > 0) {
      //handleModalSearchQueryChange(itemSearch.trim());

      const currentUser = JSON.parse(localStorage.getItem("currentUser"));

      let DEPTNO = currentUser?.[0]?.DEPTNO ? currentUser[0].DEPTNO : "-";

      getCustomerList(
        `${API_PATHS[0].API_CONFIG}/api/Search_Customer/${COMPANY_CODE}/Cust/${searchTermCustomer}/${DEPTNO}`
      );
    } else {
      setCurrentSelectedItem("-");
    }
  }, [searchTermCustomer]);

  const getTotalColumns = (data) => {
    if (!data || data.length === 0) return 1; // Default to 1 if no data

    // Dynamically determine all unique keys from the first row
    const allKeys = Object.keys(data[0]);

    // Exclude internal/helper keys used during grouping/aggregation
    const descriptiveColumnKey = getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      data
    );

    const excludedKeys = [
      "isGroupHeader",
      "isSubHeader",
      "isSubSubHeader",
      "totalAmount",
      "totalAmountExclVAT",
      descriptiveColumnKey,
    ];

    // Count the descriptive column (1) + all other data columns
    const displayKeys = allKeys.filter((key) => !excludedKeys.includes(key));

    // The total is the sticky column + the rest of the dynamic columns
    return 1 + displayKeys.length;
  };

  // --- Core Logic (Grouping, Filtering, Totals) ---

  const reportData = datagetSalesOrderReportAPI || [];

  // The data passed to the child component must be the final, filtered/grouped array.
  const filteredRows = useMemo(() => {
    console.log("reportData inside  filteredRows---->", reportData);

    // 2. Simple Filtering (for all other modes)
    // Apply your existing searchTextTable filter logic here if needed, or pass the full data.
    let finalData = reportData;

    if (searchTextTable) {
      const search = searchTextTable.toLowerCase();
      finalData = reportData.filter((row) =>
        Object.values(row).some((value) =>
          String(value).toLowerCase().includes(search)
        )
      );
    }

    return finalData;
  }, [reportData, currentSelectedModeObject, searchTextTable]);

  // Calculate the array of keys that should be totaled in the footer.
  const keysToTotal = useMemo(() => {
    if (!reportData || reportData.length === 0) return [];

    // Get all keys from the first row
    const firstRow = reportData[0];
    const allKeys = Object.keys(firstRow);

    // Filter keys based on the existing numeric header logic (now including PROFIT, COST, QTY)
    // Ensure keys are filtered correctly.
    return allKeys.filter((key) => isNumericHeader(key));
  }, [reportData]);

  // Calculate the primary descriptive key (used for the first column and footer alignment)
  const descriptiveColumnKey = useMemo(() => {
    // Recalculate the descriptive key based on the final data, not just the raw reportData
    return getDescriptiveColumnKey(
      currentSelectedModeObject?.value,
      filteredRows
    );
  }, [currentSelectedModeObject, filteredRows]);

  // Calculate totals only on the raw report data or final filtered data (Kept for PDF download logic)
  const grandColumnTotals = useMemo(() => {
    return calculateGrandTotals(reportData);
  }, [reportData]);

  // --- Component Mapping ---

  const TableToPDFMap = new Map([
    [InvoiceProfitTable, SalesTableGroupedPDF],
    [SalesTableFlatCASH, SalesTableFlatCASHPDF],
    [SalesTableMonthly, SalesTableMonthlyPDF],
    [SalesTableAggregated, SalesTableAggregatedPDF],
    [SalesTableDefault, SalesTableDefaultPDF],
  ]);

  const ReportTableMap = useMemo(
    () => ({
      // 1. GROUPED MODES (Hierarchical 3-level)
      INVPROF: DynamicTableForInvoiceProfitAnalysis,
      ITMPROF: DynamicTableForInvoiceProfitAnalysis,
    }),
    []
  );

  const clickedOnPrint = async () => {
    if (!filteredRows || filteredRows?.length === 0) return;
    if (!InvoiceProfitAnalysisReportPDF) return;

    // Open a blank window first to avoid popup block
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      alert("Popup blocked! Please allow popups for this site.");
      return;
    }

    // Render your PDF component
    const doc = (
      <InvoiceProfitAnalysisReportPDF
        rows={filteredRows}
        descriptiveColumnKey={descriptiveColumnKey}
        grandColumnTotals={grandColumnTotals}
        currentSelectedModeObject={currentSelectedModeObject}
        dateFrom={dateFrom} // 👈 Added
      dateTo={dateTo}   
      currentSelectedDepartment={currentSelectedDepartment}
        clientDetails={{
          name: "test",
          address: "test address",
          phone: "1234567890",
          email: "test@email",
          footerText: "test footer",
        }}
      />
    );

    // Generate PDF as blob
    const asPdf = pdf(doc);
    const blob = await asPdf.toBlob();

    // Create blob URL
    const blobUrl = URL.createObjectURL(blob);

    // Load the PDF in the blank window
    printWindow.location.href = blobUrl;

    // Optional: wait until the PDF loads, then print
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const CurrentTableComponent = currentSelectedModeObject
    ? DynamicTableForInvoiceProfitAnalysis
    : null;

  // --- Pagination (Remains in Parent) ---
  // const { paginatedRows, page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(filteredRows);

  const { paginatedRows, loadNextPage, hasMore, totalRows } =
    useScrollLoading(filteredRows);

  const scrollContainerRef = useRef(null);

  // --- Scroll Handler: fires loadNextPage() as the user nears the bottom
  // of the table's scroll area (the DynamicTable child no longer scrolls itself). ---
  const handleScroll = () => {
    const currentTarget = scrollContainerRef.current;
    if (!currentTarget || loadinggetSalesOrderReportAPI || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = currentTarget;

    if (scrollHeight - scrollTop <= clientHeight + 10) {
      loadNextPage();
    }
  };

  const totalColumnsCount = getTotalColumns(paginatedRows); // Calculate the number

  const clickedOnExport = async () => {
    // Check if running on the client (browser) before attempting DOM operations
    if (typeof document === "undefined") {
      console.warn(
        "PDF Export called in a non-browser environment (SSR). Skipping download."
      );
      return;
    }

    const currentSelectedMode = currentSelectedModeObject.value;

    // A. Get the original Table Component
    const ReportComponent = ReportTableMap[currentSelectedMode];

    // B. Look up the corresponding PDF Component
    const PDFComponent = ReportComponent
      ? TableToPDFMap.get(ReportComponent)
      : null;

    if (!PDFComponent) {
      console.error(`No PDF component found for mode: ${currentSelectedMode}`);
      return;
    }

    // 1. Render the PDF Document instance using the imported component
    const documentInstance = // Renamed to avoid confusion with browser's 'document'
      (
        <PDFComponent
          rows={filteredRows}
          descriptiveColumnKey={descriptiveColumnKey}
          clientDetails={{
            name: "test",
            address: "test address",
            phone: "1234567890",
            email: "test@email",
            footerText: "test footer",
          }}
        />
      );

    try {
      // 2. Use the pdf() utility to generate the blob
      const blob = await pdf(documentInstance).toBlob();

      // 3. Create a temporary URL and link element to trigger the download
      const url = URL.createObjectURL(blob);

      // Use a single, correct declaration of 'link' (DOM operation is safe here)
      const link = document.createElement("a");

      // Define the file name
      link.download = `SalesReport_${currentSelectedMode}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      link.href = url;

      // 4. Append to body, click, and clean up
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url); // Clean up the URL object
    } catch (error) {
      console.error("Error during PDF generation or download:", error);
    }
  };

  return (
    <div
      className="bg-gray-200 overflow-hidden"
      style={{ height: "calc(100vh - 50px)" }}
    >
      <div className="max-w-full mx-auto bg-white shadow-xl rounded-none border border-violet-100 flex flex-col h-full overflow-hidden">
        {/* Report Header */}
        <div className="border-b border-violet-100 px-6 py-4 bg-gradient-to-r from-violet-50 via-white to-sky-50">
          <div className="flex items-center justify-between">
            <h1
              style={{
                fontSize: "18px",
                fontWeight: 500,
                color: "#3730a3",
                textTransform: "uppercase",
              }}
              className="tracking-wide"
            >
              Invoice Profit Analysis Report
            </h1>
            <div className="flex gap-2">
              <button
                onClick={clickedOnPrint}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-sky-200 bg-white rounded-md text-xs font-semibold uppercase tracking-wide text-sky-700 hover:bg-sky-50 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.8"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M6 9V2h12v7m-9 8h6m-9 4h12v-4H6v4zm-1 0a2 2 0 0 1-2-2V11a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H5z"
                  />
                </svg>
                Print
              </button>
              {currentSelectedModeObject && filteredRows && (
                <ReportDownloadButton
                  ReportPDF={SalesTableGroupedPDF}
                  currentSelectedModeObject={currentSelectedModeObject}
                  filteredRows={filteredRows}
                  descriptiveColumnKey={descriptiveColumnKey}
                  grandColumnTotals={grandColumnTotals}
                  clientDetails={{
                    name: "test",
                    address: "test address",
                    phone: "1234567890",
                    email: "test@email",
                    footerText: "test footer",
                  }}
                >
                  Download
                </ReportDownloadButton>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Main content: Filters + Table (full width, no sidebar) */}
          <div className="flex-1 flex flex-col min-w-0 p-4 gap-3 overflow-hidden">
            {/* Filter Bar */}
            <div className="border border-violet-100 bg-violet-50/30 rounded-md p-3 shrink-0">
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex gap-3">
                  <div className="w-[220px]">
                    {/* <FieldLabel>Report Mode</FieldLabel> */}
                    <SelectInput
                      value={currentSelectedModeObject?.value || ""}
                      onChange={(e) => {
                        const chip = salesModes.find(
                          (m) => m.value === e.target.value
                        );
                        if (chip) handleChipClick(chip);
                      }}
                    >
                      {salesModes.map((chip, index) => (
                        <option key={index} value={chip.value}>
                          {chip.label}
                        </option>
                      ))}
                    </SelectInput>
                  </div>
                  <div>
                    {/* <FieldLabel>From</FieldLabel> */}
                    <TextInput
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div>
                    {/* <FieldLabel>To</FieldLabel> */}
                    <TextInput
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>

                <div className="flex gap-3 items-end">
                  <div className="w-[180px]">
                    {/* <FieldLabel>Department</FieldLabel> */}
                    <SelectInput
                      value={currentSelectedDepartment}
                      onChange={(e) =>
                        setCurrentSelectedDepartment(e.target.value)
                      }
                    >
                      <option value="-">Select Department</option>
                      {userDepartmentList?.map((group, index) => (
                        <option key={index} value={group.DEPTNO?.trim()}>
                          {group.DEPTNO?.trim()}
                        </option>
                      ))}
                    </SelectInput>
                  </div>

                  <div className="relative w-[200px]">
                    {/* <FieldLabel>Customer</FieldLabel> */}
                    <TextInput
                      placeholder="Search Customer"
                      value={text_search_customer}
                      onChange={(e) => {
                        setTextSearchCustomer(e.target.value);
                        setSearchTerm(e.target.value);
                      }}
                    />
                    {customerListData &&
                      text_search_customer?.length > 0 &&
                      customerListData?.length > 0 && (
                        <div className="absolute z-20 mt-1 w-full max-h-48 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
                          {customerListData.map((item, i) => (
                            <div
                              key={i}
                              onClick={() => {
                                setCurrentSelectedItem(item);
                                setTextSearchCustomer(item.Custname);
                                setCustomerListData(null);
                              }}
                              className="px-3 py-1.5 text-sm text-gray-700 hover:bg-violet-50 cursor-pointer border-b border-gray-100 last:border-0"
                            >
                              {item.Custname}
                            </div>
                          ))}
                        </div>
                      )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={resetFilters}
                    className="px-3 py-1.5 border border-rose-200 bg-white rounded-md text-xs font-semibold uppercase tracking-wide text-rose-500 hover:bg-rose-50 transition-colors"
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => setIsFilterPanelVisible((prev) => !prev)}
                    className="px-3 py-1.5 bg-gradient-to-r from-violet-400 to-indigo-400 text-white rounded-md text-xs font-semibold uppercase tracking-wide hover:from-violet-500 hover:to-indigo-500 transition-colors shadow-sm"
                  >
                    Filters
                  </button>
                </div>
              </div>

              {/* Expanded Filters */}
              {isFilterPanelVisible && (
                <div className="mt-4 pt-4 border-t border-violet-100 flex flex-wrap gap-4 items-end">
                  <SimpleAutocomplete
                    label="Select Group"
                    options={datagetgroup}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetgroup}
                    value={
                      datagetgroup?.find(
                        (g) => g["GRP CODE"] === currentSelectedGroup
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedGroup(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <SimpleAutocomplete
                    label="Select SubGroup"
                    options={datagetSubgroup}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetSubgroup}
                    value={
                      datagetSubgroup?.find(
                        (g) => g["GRP CODE"] === currentSelectedSubGroup
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedSubGroup(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <SimpleAutocomplete
                    label="Select Category"
                    options={datagetCategory}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetCategory}
                    value={
                      datagetCategory?.find(
                        (g) => g["GRP CODE"] === currentSelectedCategory
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedCategory(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <SimpleAutocomplete
                    label="Select SubCategory"
                    options={datagetSubCategory}
                    getOptionLabel={(option) => option?.DESCRIPTION || ""}
                    loading={loadinggetSubCategory}
                    value={
                      datagetSubCategory?.find(
                        (g) => g["GRP CODE"] === currentSelectedSubCategory
                      ) || null
                    }
                    onChange={(newValue) =>
                      setCurrentSelectedSubCategory(
                        newValue ? newValue["GRP CODE"] : "-"
                      )
                    }
                  />

                  <SimpleAutocomplete
                    label="Select Job"
                    options={datagetJobList}
                    getOptionLabel={(option) =>
                      option?.JobCode ? `${option.Description || ""}` : ""
                    }
                    value={jobCode}
                    onChange={(newValue) => setJobCode(newValue)}
                  />

                  <SimpleAutocomplete
                    label="Select Salesman"
                    options={datagetSalesmanList}
                    getOptionLabel={(option) =>
                      option?.JobCode ? `${option.Description || ""}` : ""
                    }
                    value={selectedSalesMan}
                    onChange={(newValue) => setSelectedSalesMan(newValue)}
                  />

                  <div>
                    <FieldLabel>Returns</FieldLabel>
                    <div className="flex items-center gap-4 h-[30px]">
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="sales-filter"
                          value="NORET"
                          checked={current_statusOfReturnCheckbox === "NORET"}
                          onChange={(e) =>
                            setCurrentStatusOfReturnCheckbox(e.target.value)
                          }
                          className="accent-violet-500"
                        />
                        Sales Only
                      </label>
                      <label className="flex items-center gap-1.5 text-sm text-gray-700 cursor-pointer">
                        <input
                          type="radio"
                          name="sales-filter"
                          value="RET"
                          checked={current_statusOfReturnCheckbox === "RET"}
                          onChange={(e) =>
                            setCurrentStatusOfReturnCheckbox(e.target.value)
                          }
                          className="accent-violet-500"
                        />
                        Include Return
                      </label>
                    </div>
                  </div>

                  <div>
                    <FieldLabel>FIFO</FieldLabel>
                    <button
                      type="button"
                      onClick={() => setCurrentStatusOfFifoCheckbox((v) => !v)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        current_statusOfFifoCheckbox
                          ? "bg-teal-400"
                          : "bg-gray-300"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          current_statusOfFifoCheckbox
                            ? "translate-x-6"
                            : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>

           

            {/* Table Section */}
            <div className="flex-1 min-h-0 flex flex-col border border-violet-100 rounded-md overflow-hidden">
              <div
                ref={scrollContainerRef}
                onScroll={handleScroll}
                className="flex-1 min-h-0 overflow-y-auto"
              >
                {CurrentTableComponent ? (
                  <CurrentTableComponent
                    rows={paginatedRows}
                    loading={loadinggetSalesOrderReportAPI}
                    mode={currentSelectedModeObject.value}
                    footerTotalKeys={keysToTotal}
                    groupSalesThreeLevels={groupSalesThreeLevels}
                    descriptiveColumnKey={descriptiveColumnKey}
                    totalColumnsCount={totalColumnsCount}
                    hasMore={hasMore}
                    loadNextPage={loadNextPage}
                  />
                ) : (
                  <div className="text-center py-10">
                    <p className="text-xs font-semibold text-violet-300 uppercase tracking-wide">
                      Please select a report mode from the left panel.
                    </p>
                  </div>
                )}
              </div>

              {/* Report Footer */}
              <div className="border-t-2 border-violet-200" />
              <div className="border-t border-violet-100" />
              <div className="px-4 py-2.5 flex items-center justify-between bg-gradient-to-r from-violet-50 to-sky-50">
                <p className="text-xs font-semibold text-teal-700">
                  Total Rows Found: {filteredRows.length}
                </p>
                <p className="text-xs text-gray-400">
                  Generated:{" "}
                  {new Date().toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceProfitAnalysisReport;